import axios from "axios";
import { authService } from "./authService";

const env = process.env.REACT_APP_ENVIRONMENT;
const apiBaseUrl =
  env === "LOCAL"
    ? process.env.REACT_APP_BACKEND_URL_LOCAL
    : process.env.REACT_APP_BACKEND_URL_PRODUCTION;

// Axios instance (mirrors clientService setup)
const apiClient = axios.create({
  baseURL: apiBaseUrl,
  headers: { "Content-Type": "application/json" },
  timeout: 90000,
});

// Attach auth
apiClient.interceptors.request.use(async (config) => {
  const token = await authService.acquireToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Maps the backend LeadView shape to a flat UI object used by leadDetail.jsx
const mapLeadViewToUI = ({ id, created_at, lead }, { notes = [] } = {}) => ({
  id,
  created_at,
  // Core lead fields — spread so the component can read them directly off the object
  name: lead.name ?? "",
  type: lead.type ?? "",
  company_name: lead.company_name ?? "",
  ein: lead.ein ?? "",
  trello_card: lead.trello_card ?? "",
  services: lead.services ?? [],
  status: lead.status ?? "",
  email: lead.email ?? "",
  secondary_email: lead.secondary_email ?? "",
  phone: lead.phone ?? "",
  additional_phones: lead.additional_phones ?? "",
  contact_method: lead.contact_method ?? "",
  address: lead.address ?? "",
  city: lead.city ?? "",
  international: lead.international ?? "",
  referral: lead.referral ?? "",
  // backend uses refer_partner_*, UI uses ref_partner_* — mapped here once
  ref_partner_id: lead.refer_partner_id ?? null,
  ref_partner_name: lead.refer_partner_name ?? null,
  sales_rep_id: lead.sales_rep_id ?? null,
  sales_rep_name: lead.sales_rep_name ?? null,
  language: lead.language ?? "",
  industry: lead.industry ?? "",
  urgency: lead.urgency ?? "",
  pain_point: lead.pain_point ?? "",
  // keep as null (not "") so Pydantic can accept Optional[float/int]
  annual_revenue: lead.annual_revenue ?? null,
  years_business: lead.years_business ?? null,
  accountant: lead.accountant ?? "",
  employees_number: lead.employees_number ?? null,
  sharepoint_id: lead.sharepoint_id ?? null,
  documents_url: lead.documents_url ?? null,
  service_ids: lead.service_ids ?? [],
  service_names: lead.service_names ?? [],
  notes,
});

class LeadService {
  // -------- Leads --------

  /**
   * List / search leads.
   * @param {string}   search       - free-text search (name, email, phone, company)
   * @param {number}   limit
   * @param {number}   skip
   * @param {object}   filters      - { status?: string[], salesRep?: string[] }
   * @returns {{ items: LeadView[], total: number, limit: number, skip: number }}
   */
  getLeads = async (search = "", limit = 50, skip = 0, filters = {}) => {
    const params = new URLSearchParams({
      limit: String(limit),
      skip: String(skip),
    });
    if (search) params.append("search", search);
    filters.status?.forEach((s) => params.append("status", s));
    filters.salesRep?.forEach((name) => params.append("sales_rep_name", name));
    const { data } = await apiClient.get(`/api/leads?${params}`);
    return data; // { items: LeadView[], total, limit, skip }
  };

  /**
   * Fetch a single lead with its notes hydrated.
   * Used by the commented-out loadLead() in leadDetail.jsx.
   */
  getLead = async (leadId) => {
    const [{ data: lv }, notes] = await Promise.all([
      apiClient.get(`/api/leads/${leadId}`),
      this.listNotes(leadId),
    ]);
    return mapLeadViewToUI(lv, { notes });
  };

  /**
   * Create a lead.
   * payload shape: { lead: { name, company_name, services[], status, email, ... } }
   */
  createLead = async (payload) => {
    try {
      const { data } = await apiClient.post("/api/leads", payload);
      return data; // LeadView
    } catch (err) {
      const msg = err.response?.data?.detail || "Failed to create lead";
      throw new Error(msg);
    }
  };

  /**
   * Update a lead.
   * payload shape: { lead: { ...fields... } }
   * Handles frontend→backend key remapping and empty-string→null coercion.
   */
  updateLead = async (leadId, payload) => {
    const { ref_partner_id, ref_partner_name, ...rest } = payload.lead || {};

    // Numeric fields: convert "" → null so Pydantic Optional[float/int] accepts them
    const numericNullify = (v) => (v === "" || v === undefined ? null : v);

    const backendLead = {
      ...rest,
      refer_partner_id: ref_partner_id ?? null,
      refer_partner_name: ref_partner_name ?? null,
      annual_revenue: numericNullify(rest.annual_revenue),
      years_business: numericNullify(rest.years_business),
      employees_number: numericNullify(rest.employees_number),
    };

    try {
      const { data } = await apiClient.put(`/api/leads/${leadId}`, {
        lead: backendLead,
      });
      return data; // LeadView
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map((e) => `${e.loc?.join(".")} — ${e.msg}`).join("; ")
        : detail || "Failed to update lead";
      throw new Error(msg);
    }
  };

  /** Delete a lead (cascades to notes). */
  deleteLead = async (leadId) => {
    const res = await apiClient.delete(`/api/leads/${leadId}`);
    return res.status === 204 ? { ok: true } : { ok: false };
  };

  getServices = async (q = "") => {
    const params = new URLSearchParams();
    if (q) params.append("q", q);
    const { data } = await apiClient.get(`/api/services?${params}`);
    return data; // [{ id, name }]
  };

  createService = async (name) => {
    const { data } = await apiClient.post(`/api/services`, { name });
    return data; // { id, name }
  };

  deleteService = async (serviceId) => {
    const { data } = await apiClient.delete(`/api/services/${serviceId}`);
    return data;
  };

  getReferPartners = async (q = "") => {
    const params = new URLSearchParams();
    if (q) params.append("q", q);
    const { data } = await apiClient.get(`/api/refer-partners?${params}`);
    return data; // [{ id, name }]
  };

  createReferPartner = async (name) => {
    const { data } = await apiClient.post(`/api/refer-partners`, { name });
    return data; // { id, name }
  };

  deleteReferPartner = async (referPartnerId) => {
    const { data } = await apiClient.delete(
      `/api/refer-partners/${referPartnerId}`,
    );
    return data;
  };

  /**
   * Export all leads as an Excel file (blob).
   * Used by handleExport() in leadList.jsx.
   */
  exportLeads = async ({ search } = {}) => {
    const params = new URLSearchParams();
    if (search) params.set("search", search);

    const res = await apiClient.get(`/api/leads/export?${params.toString()}`, {
      responseType: "blob",
      headers: {
        Accept:
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      },
      validateStatus: (s) => s >= 200 && s < 300,
    });

    const ct = (res.headers && res.headers["content-type"]) || "";
    const isExcel = ct.includes(
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    );

    if (!isExcel) {
      try {
        const text = await new Response(res.data).text();
        throw new Error(
          `Export failed. Content-Type: ${ct}. Body: ${text.slice(0, 300)}`,
        );
      } catch {
        throw new Error(`Export failed. Content-Type: ${ct}.`);
      }
    }

    return res.data; // Blob
  };

  // -------- Lead Notes --------

  /** List all notes for a lead. */
  listNotes = async (leadId) => {
    const { data } = await apiClient.get(`/api/leads/${leadId}/notes`);
    return data; // LeadNoteOut[]
  };

  /**
   * Add a note to a lead.
   * Accepts a string (used as content) or a full note object.
   */
  addNote = async (leadId, note) => {
    let body;
    if (typeof note === "string") {
      body = { title: "Note", content: note };
    } else if (note && typeof note === "object") {
      const { title, content, author, who_updated_last, asset_url } = note;
      body = {
        title: title ?? "Note",
        content,
        author,
        who_updated_last,
        asset_url,
      };
    } else {
      throw new Error("Invalid note payload");
    }
    const { data } = await apiClient.post(`/api/leads/${leadId}/notes`, body);
    return data; // LeadNoteOut
  };

  /** Update an existing note. */
  updateNote = async (
    leadId,
    noteId,
    { title, content, who_updated_last, asset_url = null },
  ) => {
    const { data } = await apiClient.put(
      `/api/leads/${leadId}/notes/${noteId}`,
      { title, content, who_updated_last, asset_url },
    );
    return data; // LeadNoteOut
  };

  /** Delete a note (also cleans up the attached file on the server). */
  deleteNote = async (leadId, noteId) => {
    const res = await apiClient.delete(`/api/leads/${leadId}/notes/${noteId}`);
    return res.status === 204 ? { ok: true } : { ok: false };
  };

  /**
   * Upload a file attachment for a note.
   * Returns { message, file_name, path }.
   */
  uploadNoteAttachment = async (leadId, { title, author, file }) => {
    const form = new FormData();
    form.append("title", title);
    form.append("author", author);
    form.append("file", file);

    const { data } = await apiClient.post(
      `/api/leads/${leadId}/notes/upload`,
      form,
      { headers: { "Content-Type": "multipart/form-data" } },
    );
    return data; // { message, file_name, path }
  };

  /**
   * Stream / preview a note attachment.
   * Returns { blob: Blob, contentType: string }.
   * Used by loadPreview() in leadDetail.jsx.
   */
  getNoteFile = async (leadId, path) => {
    const res = await apiClient.get(`/api/leads/${leadId}/notes/file`, {
      params: { path },
      responseType: "blob",
      validateStatus: (s) => s >= 200 && s < 300,
    });
    const blob = res.data;
    const contentType =
      res.headers["content-type"] || "application/octet-stream";
    return { blob, contentType };
  };

  // -------- Lead Credentials --------
  getCredentials = async (leadId) => {
    const { data } = await apiClient.get(`/api/leads/${leadId}/credentials`);
    return data;
  };
  createCredential = async (leadId, cred) => {
    const { data } = await apiClient.post(
      `/api/leads/${leadId}/credentials`,
      cred,
    );
    return data;
  };
  updateCredential = async (leadId, credId, cred) => {
    const { data } = await apiClient.put(
      `/api/leads/${leadId}/credentials/${credId}`,
      cred,
    );
    return data;
  };
  deleteCredential = async (leadId, credId) => {
    return apiClient.delete(`/api/leads/${leadId}/credentials/${credId}`);
  };

  // -------- Lead QuickBooks --------
  getQuickBooks = async (leadId) => {
    const { data } = await apiClient.get(`/api/leads/${leadId}/quickbooks`);
    return data;
  };
  upsertQuickBooks = async (leadId, qb) => {
    const { data } = await apiClient.post(
      `/api/leads/${leadId}/quickbooks`,
      qb,
    );
    return data;
  };
  deleteQuickBooks = async (leadId) => {
    return apiClient.delete(`/api/leads/${leadId}/quickbooks`);
  };
}

export const leadService = new LeadService();
